import { http } from "./http";

export type FrigomatModel = {
  id: number;
  codice: number;
  descrizione: string;
  name: string;
  weightDivider: number | null;
  defaultRecipeIdMin: number | null;
  defaultRecipeIdMax: number | null;
  customRecipeIdMin: number | null;
  customRecipeIdMax: number | null;
  recipeType: { id: number; code: number; description: string } | null;
};

export type FrigomatMachineType = {
  id: number;
  descrizione: string;
  ordinamento: number;
  modelType: number;
  modelType2: number | null;
};

export type FrigomatMachine = {
  maC_Id?: number;
  maC_Numero_Serie_MS?: number;
  maC_Matricola?: string;
  maC_Macchina?: number;
  maC_ModelloSx?: number;
  maC_ModelloDx?: number | null;
  maC_LastUpdate?: string;
  maC_VersSwInt?: string;
  maC_VersSwQSx?: string;
  maC_Grammi?: number;
  /** Indirizzo IP logico della macchina (cloud WiManager) */
  maC_IP?: string;
  maC_IndirizzoIP?: string;
  maC_IpAddress?: string;
  /** Segnale WiFi in dBm (es. -65) */
  maC_WiFiRSSI?: number;
  maC_RSSI?: number;
  maC_SegnaleWifi?: number;
  maC_SignalStrength?: number;
  /** Tipo di connessione (WiFi, Ethernet, ecc.) */
  maC_TipoConnessione?: string;
  maC_ConnectionType?: string;
  [key: string]: unknown;
};

/** Fallback da meta (sync) quando il dettaglio API non include i campi */
type ConnectionInfoFallback = {
  ipAddress?: string | null;
  wifiSignalDb?: number | null;
  connectionType?: string | null;
};

/** Estrae info di connessione (IP, segnale WiFi, linea) dal dettaglio WiManager o da meta */
export function getFrigomatConnectionInfo(
  d: FrigomatMachine | null | undefined,
  fallback?: ConnectionInfoFallback | null,
): { ipAddress: string | null; wifiSignalDb: number | null; connectionType: string | null } {
  const fromDetail = (obj: Record<string, unknown> | null | undefined) => {
    if (!obj || typeof obj !== "object") return { ipAddress: null, wifiSignalDb: null, connectionType: null };
    const ip = obj.maC_IP ?? obj.maC_IndirizzoIP ?? obj.maC_IpAddress ?? obj.ip ?? obj.ipAddress;
    const ipStr = typeof ip === "string" ? ip.trim() : typeof ip === "number" ? String(ip) : null;
    const wifi =
      obj.maC_WiFiRSSI ?? obj.maC_RSSI ?? obj.maC_SegnaleWifi ?? obj.maC_SignalStrength ??
      obj.wifiRssi ?? obj.rssi;
    const wifiNum = typeof wifi === "number" ? wifi : typeof wifi === "string" ? parseFloat(wifi) : null;
    const conn = obj.maC_TipoConnessione ?? obj.maC_ConnectionType ?? obj.connectionType ?? null;
    return {
      ipAddress: ipStr || null,
      wifiSignalDb: Number.isFinite(wifiNum) ? wifiNum : null,
      connectionType: typeof conn === "string" ? conn : null,
    };
  };
  const det = fromDetail(d as Record<string, unknown>);
  if (fallback && (det.ipAddress == null && det.wifiSignalDb == null && det.connectionType == null)) {
    return {
      ipAddress: fallback.ipAddress ?? null,
      wifiSignalDb: fallback.wifiSignalDb ?? null,
      connectionType: fallback.connectionType ?? null,
    };
  }
  return det;
}

export type FrigomatAlarmEntry = {
  saL_Id?: number;
  saL_MAC_Numero_Serie_MS?: number;
  saL_DateTime?: string;
  saL_Allarme?: number;
  saL_SxDx?: number;
  saL_Storico?: unknown;
};

export const frigomatApi = {
  getModels: (token: string | null, accountId?: string) => {
    const path = accountId
      ? `/integrations/frigomat/models/${accountId}`
      : "/integrations/frigomat/models";
    return http<FrigomatModel[]>(path, { method: "GET" }, token);
  },

  getMachineListCatalog: (token: string | null, accountId?: string) => {
    const path = accountId
      ? `/integrations/frigomat/machine-list/${accountId}`
      : "/integrations/frigomat/machine-list";
    return http<FrigomatMachineType[]>(path, { method: "GET" }, token);
  },

  getMachines: (token: string | null, accountId?: string) => {
    const q = accountId ? `?accountId=${encodeURIComponent(accountId)}` : "";
    return http<FrigomatMachine[]>(`/integrations/frigomat/machines${q}`, { method: "GET" }, token);
  },

  getMachineDetail: (token: string | null, serialMs: string, accountId?: string) => {
    const q = accountId ? `?accountId=${encodeURIComponent(accountId)}` : "";
    return http<FrigomatMachine | null>(
      `/integrations/frigomat/machine/${encodeURIComponent(serialMs)}${q}`,
      { method: "GET" },
      token
    );
  },

  getAlarmsHistory: (token: string | null, serialMs: string, accountId?: string) => {
    const q = accountId ? `?accountId=${encodeURIComponent(accountId)}` : "";
    return http<FrigomatAlarmEntry[]>(
      `/integrations/frigomat/alarms-history/${encodeURIComponent(serialMs)}${q}`,
      { method: "GET" },
      token
    );
  },

  getI18n: (token: string | null) =>
    http<Record<string, string>>("/integrations/frigomat/i18n", { method: "GET" }, token),

  getAppToken: (token: string | null, siteId: string | number, accountId?: string) => {
    const q = accountId ? `?accountId=${encodeURIComponent(accountId)}` : "";
    return http<string | null | Record<string, unknown>>(
      `/integrations/frigomat/app-token/${encodeURIComponent(String(siteId))}${q}`,
      { method: "GET" },
      token
    );
  },

  getRealtimeInfo: (token: string | null, accountId?: string) => {
    const q = accountId ? `?accountId=${encodeURIComponent(accountId)}` : "";
    return http<{
      siteId: string | null;
      appToken: string | null | Record<string, unknown>;
      realtimeUrl: string;
    }>(`/integrations/frigomat/realtime-info${q}`, { method: "GET" }, token);
  },
};
